import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Traps Tab focus inside the returned ref while `active`, moves focus into
 * the dialog on open, restores it to the previously focused element on
 * close, and calls `onClose` on Escape. For overlay components rendered
 * with role="dialog" aria-modal="true" (CartDrawer, DeliveryModal,
 * SearchModal, …) — without this, keyboard/screen-reader users can tab
 * "through" the overlay into the page behind it, and Escape does nothing.
 */
export function useFocusTrap<T extends HTMLElement>(active: boolean, onClose: () => void) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    if (!active) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const container = ref.current;

    const focusables = () =>
      container ? Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter(element => !element.closest('[inert]') && element.getClientRects().length > 0) : [];

    if (!container?.contains(document.activeElement)) {
      const preferredFocus = container?.querySelector<HTMLElement>('[data-autofocus]');
      (preferredFocus ?? focusables()[0] ?? container)?.focus({ preventScroll: true });
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const items = focusables();
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    // Listen during capture so Escape still reaches an open dialog when a
    // nested component or a third-party widget stops bubbling keyboard events.
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      // A newer panel may already own focus while this one finishes exiting.
      if (document.activeElement === document.body || container?.contains(document.activeElement)) {
        previouslyFocused?.focus?.({ preventScroll: true });
      }
    };
  }, [active, onClose]);

  return ref;
}
